// libraries
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import pretty from '@st-operations/uikit/lib/pretty'

// css
import './Form.scss'

// components
import { EntitySelect, TextArea, Switch, Input } from '@st-operations/uikit/src/v2/index.js'
import CustomSelect from '../custom-select'

// component
class Form extends Component {
  // key can have one or more parts (ex: destinations.name)
  // required value could be string or array
  // "Array" is referring to { multi: true }
  //
  //  condition: {
  //    "destinations.name": ["foo", "bar"]
  //  }

  schemaConditionIsMet(item, schema, condition) {
    return Object.keys(condition).every((key) => {
      // field = "destinations", name = "name"
      const [field, name] = key.split('.')

      // confirm both the field and schema know about this field
      if (!item[field] || !schema[field]) {
        return false
      }

      // If it's a string, check if it's the same
      if (typeof item[field] === 'string' && typeof condition[key] === 'string') {
        return item[field].toLowerCase() === condition[key].toLowerCase()
      }

      // ex: item.destinations
      const item_values = [item[field]].flat()

      // get the expected value of the condition
      // ex: ["foo", "bar"]
      const condition_values = [condition[key]].flat()

      // check if the field is declared in the schema as an array
      for (let i = 0; i < item_values.length; i++) {
        let value = item_values[i]
        if (name) {
          value = value[name]
        }
        if ([condition_values].flat().includes(value)) {
          return true
        }
      }

      // the condition wasn't met
      return false
    })
  }

  renderFields = (fields) => {
    const {
      disabled,
      schema,
      ordered,
      saveOnChange,
      item,
      onChange,
      slim,
      small,
      hideLabel,
    } = this.props

    if (fields) {
      fields.forEach((field, index) => {
        if (typeof field === 'string') {
          fields[index] = { name: field }
        }
      })
    }

    // apply "enum", "label" "multi", "collection", "description" from schema
    if (fields && schema) {
      for (let field of fields) {
        if (schema[field.name]) {
          if (schema[field.name].collection) {
            field.entity = schema[field.name].collection
          }
          if (schema[field.name].label) {
            field.label = field.label || schema[field.name].label
          }
          if (schema[field.name].enum) {
            field.options = schema[field.name].enum
          }
          if (schema[field.name].description) {
            field.description = schema[field.name].description
          }
          if (schema[field.name].disabled || disabled) {
            field.disabled = schema[field.name].disabled || disabled
          }
          if (schema[field.name].type) {
            field.type = schema[field.name].type
          }
          field.multi = schema[field.name].multi || false
        }
        if (disabled) {
          field.disabled = disabled
        }
        if (saveOnChange) {
          field.saveOnChange = saveOnChange
        }
      }
    }

    return (
      <For each="field" index="key" of={fields}>
        <If key={key} condition={field.condition === undefined || field.condition}>
          <Choose>
            <When condition={field.group}>
              <div className={field.className}>{this.renderFields(field.fields)}</div>
            </When>
            {/* Entity Select */}
            <When condition={field.render}>
              {field.render({
                disabled,
                schema,
                saveOnChange,
                item,
                onChange,
                slim,
                small,
                field,
              })}
            </When>
            <When condition={field.entity}>
              <EntitySelect
                className={field.className}
                description={field.description}
                disabled={field.disabled}
                entity={field.entity}
                isClearable={true}
                isMulti={field.multi}
                isSearchable={true}
                key={key}
                label={field.label || pretty(field.name)}
                name={field.name}
                onChange={onChange}
                options={field.options}
                saveOnChange={field.saveOnChange}
                small={small}
                value={item && item[field.name]}
              />
            </When>

            {/* Select */}
            <When condition={field.options}>
              <CustomSelect
                className={field.className}
                description={field.description}
                disabled={field.disabled}
                isClearable={true}
                isMulti={field.multi}
                isSearchable={true}
                key={key}
                label={field.label || pretty(field.name)}
                placeholder={field.placeholder}
                name={field.name}
                onChange={onChange}
                options={
                  ordered?.includes(field.name)
                    ? Object.entries(field.options)
                        .map(([key, value]) => ({ value: key, label: value }))
                        .sort((a, b) => (a.label > b.label ? 1 : -1))
                    : field.options
                }
                saveOnChange={field.saveOnChange}
                slim={slim}
                small={small}
                normal={field.normal}
                value={item && item[field.name]}
                pretty={field.pretty}
                optionAll={field.optionAll}
                menuPosition={field.menuPosition}
                menuPortalTarget={field.menuPortalTarget}
                styles={field.styles}
                useReactSelect
                hideLabel={hideLabel}
              />
            </When>

            {/* TextArea */}
            <When condition={field.type === 'text'}>
              <TextArea
                className={field.className}
                description={field.description}
                disabled={field.disabled}
                key={key}
                label={field.label || pretty(field.name)}
                name={field.name}
                onChange={onChange}
                small={small}
                value={item && item[field.name]}
              />
            </When>

            {/* Switch */}
            <When condition={field.type === 'switch'}>
              <Switch
                containerClass={field.className}
                checked={item && item[field.name]}
                key={key}
                label={field.label || pretty(field.name)}
                onChange={onChange}
              />
            </When>

            {/* Input */}
            <Otherwise>
              <Input
                className={field.className}
                description={field.description}
                disabled={field.disabled}
                key={key}
                label={field.label || pretty(field.name)}
                name={field.name}
                onChange={onChange}
                small={small}
                type={field.type}
                value={item && item[field.name] ? item && item[field.name] : ''}
              />
            </Otherwise>
          </Choose>
        </If>
      </For>
    )
  }

  render() {
    let { children, fields, className, slim, small } = this.props

    // styling
    if (slim) {
      className += ' slim'
    }
    if (small) {
      className += ' small'
    }

    return (
      <div className={`st-form ${className}`.trim()}>
        {this.renderFields(fields)}
        <If condition={children}>{children}</If>
      </div>
    )
  }
}

Form.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  fields: PropTypes.array,
  item: PropTypes.object,
  onChange: PropTypes.func,
  saveOnChange: PropTypes.bool,
  schema: PropTypes.object,
  slim: PropTypes.bool,
  small: PropTypes.bool,
  hideLabel: PropTypes.bool,
}

Form.defaultProps = {
  children: undefined,
  className: '',
  disabled: false,
  fields: [],
  item: {},
  onChange: () => {},
  saveOnChange: false,
  schema: {},
  slim: false,
  small: false,
  hideLabel: false,
}

// export
export default Form
